import { FormDatePicker, FormInput, FormSelect } from "@/components";
import useFilter from "./useFilter";
import { stateList } from "@/consts";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  CITY,
  CONTACT_NO,
  DEAL_CLOSED_BY,
  ONBOARDING_DATE,
  SCHOOL_NAME,
  STATE,
} from "./consts";
import { X } from "lucide-react";
import { FC } from "react";
import { FilterProps } from "./types";
import { useProfilesByRoleQuery } from "@/services/employeeManagement/useProfilesByRoleQuery";
import { LOGIN_ROLES } from "@/lib/utils";

const Filter: FC<FilterProps> = ({ onCancel, filterData, setFilterData }) => {
  const { form, onSubmit, requiredFields, isMobile, onClear } = useFilter({
    filterData,
    setFilterData,
    onCancel,
  });
  const { errors } = form.formState;

  // Fetch salesops employees
  const { data: salesOpsEmployeesData } = useProfilesByRoleQuery(
    LOGIN_ROLES.SALES_OPS
  );

  const dealClosedByOptions =
    salesOpsEmployeesData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">Filter</DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 gap-[25px]">
                <FormInput
                  name={SCHOOL_NAME}
                  control={form.control}
                  label="School Name"
                  placeholder="Please enter"
                  error={errors[SCHOOL_NAME]?.message}
                  isRequired={requiredFields.includes(SCHOOL_NAME)}
                />
                <FormInput
                  name={CONTACT_NO}
                  control={form.control}
                  label="Contact No"
                  placeholder="Please enter"
                  error={errors[CONTACT_NO]?.message}
                  isRequired={requiredFields.includes(CONTACT_NO)}
                />
                <FormSelect
                  name={STATE}
                  control={form.control}
                  label="State"
                  placeholder="Please enter"
                  error={errors[STATE]?.message}
                  isRequired={requiredFields.includes(STATE)}
                  options={Object.keys(stateList).map((val: string) => ({
                    label: val,
                    value: val,
                  }))}
                />
                <FormSelect
                  name={CITY}
                  control={form.control}
                  label="City"
                  placeholder="Please Select"
                  error={errors[CITY]?.message}
                  isRequired={requiredFields.includes(CITY)}
                  options={
                    form.watch(STATE)
                      ? (
                          stateList[
                            form.watch(STATE) as keyof typeof stateList
                          ] ?? []
                        ).map((val: string) => ({
                          label: val,
                          value: val,
                        }))
                      : []
                  }
                />
                <FormDatePicker
                  name={ONBOARDING_DATE}
                  control={form.control}
                  label="Onboarding Date"
                  placeholder="Please enter"
                  error={errors[ONBOARDING_DATE]?.message}
                  isRequired={requiredFields.includes(ONBOARDING_DATE)}
                />
                <FormSelect
                  name={DEAL_CLOSED_BY}
                  control={form.control}
                  label="Deal Closed By"
                  placeholder="Please Select"
                  error={errors[DEAL_CLOSED_BY]?.message}
                  isRequired={requiredFields.includes(DEAL_CLOSED_BY)}
                  options={dealClosedByOptions}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 absolute bottom-3 w-full bg-white pb-[25px] z-[3]">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  type="button"
                  className="text-red-600"
                  onClick={onClear}
                >
                  Clear All
                </Button>
              </div>
              <Button type="submit">Apply Filter</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default Filter;
